/**
 * Preselect Recharge subscription on the PDP when ?selling_plan= is present.
 * Waits for the widget to render (including shadow DOM) before selecting.
 */
(function () {
  const sellingPlanId = new URLSearchParams(window.location.search).get('selling_plan');
  if (!sellingPlanId) return;

  const MAX_MS = 10000;
  const RETRY_MS = 250;
  const STABILIZE_MS = 2000;
  const startedAt = Date.now();
  let appliedAt = null;
  let observer = null;
  let intervalId = null;

  function getScope() {
    return document.querySelector('product-info') || document;
  }

  function forEachRootNode(root, callback) {
    if (!root) return;
    callback(root);

    const elements = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (const element of elements) {
      if (element.shadowRoot) {
        forEachRootNode(element.shadowRoot, callback);
      }
    }
  }

  function sellingPlanApplied(scope) {
    let applied = false;

    forEachRootNode(scope, (root) => {
      root.querySelectorAll?.('input[name="selling_plan"], select[name="selling_plan"]').forEach((field) => {
        if (String(field.value) === String(sellingPlanId)) applied = true;
      });
    });

    return applied;
  }

  function setSellingPlanSelect(root) {
    forEachRootNode(root, (scope) => {
      scope
        .querySelectorAll?.(
          'select[name="selling_plan"], .rc-selling-plans select, select.rc-plans-select, select.rc-plans-dropdown__select'
        )
        .forEach((select) => {
          const matchingOption = Array.from(select.options).find(
            (option) =>
              String(option.value) === String(sellingPlanId) ||
              String(option.dataset.sellingPlanId) === String(sellingPlanId)
          );

          if (matchingOption && select.value !== matchingOption.value) {
            select.value = matchingOption.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
    });
  }

  function setProductFormSellingPlan(scope) {
    let updated = false;

    scope.querySelectorAll?.('form input[name="selling_plan"]').forEach((input) => {
      if (String(input.value) !== String(sellingPlanId)) {
        input.value = sellingPlanId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        updated = true;
      }
    });

    return updated;
  }

  function activateSubscriptionOption(option, root) {
    if (!option) return false;

    if ((option.type === 'radio' || option.type === 'checkbox') && !option.checked) {
      option.click();
    } else if (option.type !== 'radio' && option.type !== 'checkbox') {
      option.click();
    }

    setSellingPlanSelect(root);
    return true;
  }

  function clickSubscriptionOption(root) {
    const subscriptionSelectors = [
      '.rc-purchase-option__subscription input[type="radio"]',
      '.rc-purchase-option__subscription .rc-purchase-option__input',
      '.rc_widget__option--autodeliver input[type="radio"]',
      '.rc_widget__option--autodeliver',
      '.rc-option--subscription input[type="radio"]',
      '[data-label="subscription"] input[type="radio"]',
      '[data-option-type="subscription"] input[type="radio"]',
      'button[aria-label*="Subscribe" i]',
      'label[for*="subscription" i]',
    ];

    for (const selector of subscriptionSelectors) {
      let option;
      try {
        option = root.querySelector(selector);
      } catch (error) {
        continue;
      }
      if (option && activateSubscriptionOption(option, root)) return true;
    }

    const radios = root.querySelectorAll?.(
      '.rc-template__radio-group .rc-radio, .rc-radio-group__options .rc-radio, [role="radio"], input[type="radio"]'
    );
    if (!radios?.length) return false;

    for (const radio of radios) {
      if (!/subscribe|subscription|autodeliver|save/i.test(radio.textContent || radio.getAttribute?.('aria-label') || '')) {
        continue;
      }
      const option =
        radio.matches?.('input[type="radio"], input[type="checkbox"], label, button')
          ? radio
          : radio.querySelector('input[type="radio"], input[type="checkbox"], label, button') || radio;
      if (activateSubscriptionOption(option, root)) return true;
    }

    if (radios.length >= 2) {
      const second = radios[1];
      const option =
        second.matches?.('input[type="radio"], input[type="checkbox"], label, button')
          ? second
          : second.querySelector('input[type="radio"], input[type="checkbox"], label, button') || second;
      return activateSubscriptionOption(option, root);
    }

    return false;
  }

  function clickSubscriptionOptionInRoot(node) {
    if (clickSubscriptionOption(node)) return true;

    const elements = node.querySelectorAll ? node.querySelectorAll('*') : [];
    for (const element of elements) {
      if (element.shadowRoot && clickSubscriptionOptionInRoot(element.shadowRoot)) {
        return true;
      }
    }

    return false;
  }

  function selectRechargeSubscription(scope) {
    const widget = scope.querySelector?.('recharge-subscription-widget');
    if (widget && clickSubscriptionOptionInRoot(widget)) return true;
    if (clickSubscriptionOptionInRoot(scope)) return true;
    return setProductFormSellingPlan(scope);
  }

  function tryPreselect() {
    const scope = getScope();
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget) return false;

    selectRechargeSubscription(scope);
    return sellingPlanApplied(scope);
  }

  function cleanup() {
    observer?.disconnect();
    observer = null;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  function tick() {
    const applied = tryPreselect();

    if (applied && !appliedAt) {
      appliedAt = Date.now();
    }

    // Re-apply briefly after success — Recharge often re-renders and clears selection.
    if (appliedAt && Date.now() - appliedAt < STABILIZE_MS) {
      tryPreselect();
    }

    const timedOut = Date.now() - startedAt > MAX_MS;
    const stabilized = appliedAt && Date.now() - appliedAt >= STABILIZE_MS;
    if (timedOut || stabilized) cleanup();
  }

  function start() {
    tick();

    observer = new MutationObserver(tick);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    intervalId = window.setInterval(tick, RETRY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
